import { TStatus } from "../types";

interface IProposalProgress {
  createdDate: Date;
  startVotingDate: Date;
  endVotingDate: Date;
  status: string;
}

export function computeProposalProgress(proposal: IProposalProgress): number {
  const now = new Date();

  // Ensure dates are valid
  const { createdDate, startVotingDate, endVotingDate } = proposal;
  if (
    !createdDate ||
    !startVotingDate ||
    !endVotingDate ||
    isNaN(createdDate.getTime()) ||
    isNaN(startVotingDate.getTime()) ||
    isNaN(endVotingDate.getTime())
  ) {
    throw new Error("Invalid dates provided in the proposal.");
  }

  let progress = 0;

  switch (proposal.status) {
    case "pending":
      // Time elapsed from creation to now, relative to total time from creation to voting start
      const totalPendingTime =
        startVotingDate.getTime() - createdDate.getTime();
      const elapsedPendingTime = now.getTime() - createdDate.getTime();

      if (totalPendingTime <= 0) {
        progress = 100;
      } else {
        progress = (elapsedPendingTime / totalPendingTime) * 100;
        progress = Math.min(Math.max(progress, 0), 100);
      }
      break;

    case "active":
      // Time elapsed from voting start to now, relative to total voting period
      const totalActiveTime =
        endVotingDate.getTime() - startVotingDate.getTime();
      const elapsedActiveTime = now.getTime() - startVotingDate.getTime();

      if (totalActiveTime <= 0) {
        progress = 100;
      } else {
        progress = (elapsedActiveTime / totalActiveTime) * 100;
        progress = Math.min(Math.max(progress, 0), 100);
      }
      break;

    case "closed":
      // Progress is 100% for closed proposals
      progress = 100;
      break;

    default:
      throw new Error(`Unknown proposal status: ${proposal.status}`);
  }

  // Round progress to two decimal places
  progress = Math.round(progress * 100) / 100;

  return progress;
}

export function processProposalsByMonth(
  proposals: { creation_date: string; gov_platforms: { on_chain: boolean } }[],
): [number, number][] {
  // Initialize a 2D array for 12 months, each month having [onChainCount, offChainCount].
  const monthsData: [number, number][] = Array.from({ length: 12 }, () => [
    0, 0,
  ]);

  // Iterate over each proposal
  proposals.forEach((proposal) => {
    // Parse the creation date
    const creationDate = new Date(proposal.creation_date);
    const monthIndex = creationDate.getUTCMonth(); // Get month index (0-11)

    // Determine if the proposal is on-chain or off-chain
    const isOnChain = proposal.gov_platforms.on_chain;

    // Increment the appropriate counter for the month
    if (isOnChain) {
      monthsData[monthIndex][0] += 1; // Increment on-chain count
    } else {
      monthsData[monthIndex][1] += 1; // Increment off-chain count
    }
  });

  return monthsData;
}

export const sanitizeProposalTitle = (title: any): string => {
  if (typeof title !== "string") return "";

  // Trim whitespace first
  let sanitizedTitle = title.trim();

  // Remove leading '#' if present
  sanitizedTitle = sanitizedTitle.startsWith("#")
    ? sanitizedTitle.slice(1)
    : sanitizedTitle;

  // Remove all special characters except markdown-safe ones and [, ], .
  sanitizedTitle = sanitizedTitle.replace(/[^\w\s#*_~()`\[\]\.]/g, "");

  // Trim whitespace from the start and end
  return sanitizedTitle.trim();
};

export function getResultsMessage(status: TStatus) {
  switch (status) {
    case "pending":
      return "Voting has not started yet. Data will be available once voting begins.";
    case "active":
      return "Voting is currently in progress. Data is updated as it becomes available.";
    case "closed":
      return "No voting data available for this proposal.";
    default:
      return "";
  }
}
